import { EmptyState } from "@/components/EmptyState";
import { LoadingPanel } from "@/components/LoadingPanel";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useComplianceDocs } from "@/hooks/useComplianceDocs";
import { useSignedUrl } from "@/hooks/useSignedUrl";
import { useTenancy } from "@/hooks/useTenancy";
import { formatDateGB } from "@/lib/format";
import type { ComplianceDocType } from "@/lib/supabase";
import { ExternalLink, ShieldCheck } from "lucide-react";
import { useState } from "react";

const DOC_LABELS: Record<ComplianceDocType, { title: string; description: string }> = {
  CP12: {
    title: "CP12 Gas Safety",
    description: "Annual landlord gas safety record.",
  },
  CP12_BOILER_SERVICE: {
    title: "CP12 Gas Safety + Boiler Service",
    description: "Annual gas safety record combined with a full boiler service.",
  },
  CP6: {
    title: "Boiler Service (CP6)",
    description: "Annual gas-safe boiler service.",
  },
  EPC: {
    title: "EPC",
    description: "Energy performance certificate.",
  },
  EICR: {
    title: "EICR",
    description: "Electrical installation condition report.",
  },
};

export function PropertyPage() {
  const { tenancies, loading } = useTenancy();
  if (loading) return <LoadingPanel />;
  if (tenancies.length === 0) {
    return <EmptyState title="No property assigned" />;
  }
  return (
    <div className="flex flex-col gap-6">
      <header>
        <span className="text-xs uppercase tracking-[0.18em] text-ink-dim">Property</span>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mt-2">Your property</h1>
      </header>
      {tenancies.map(({ tenancy, property }) => (
        <PropertySection key={tenancy.id} property={property} />
      ))}
    </div>
  );
}

function PropertySection({
  property,
}: {
  property: import("@/lib/supabase").Property;
}) {
  const { data, loading, error } = useComplianceDocs(property.id);
  const { fetchUrl, pending } = useSignedUrl();
  const [openingType, setOpeningType] = useState<ComplianceDocType | null>(null);

  async function open(path: string, type: ComplianceDocType) {
    setOpeningType(type);
    const { url, error: e } = await fetchUrl(path);
    setOpeningType(null);
    if (e || !url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>{property.display_name}</CardTitle>
          <CardDescription>
            {[property.line1, property.line2, property.postcode].filter(Boolean).join(", ")}
          </CardDescription>
        </CardHeader>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-accent" />
            Compliance documents
          </CardTitle>
          <CardDescription>
            CP12, EPC, and EICR for this property. Open in a new tab; links expire after one minute.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingPanel label="Loading documents…" />
          ) : error ? (
            <p role="alert" className="text-sm text-red-300">
              {error}
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {(["CP12", "EPC", "EICR"] as ComplianceDocType[]).map((t) => {
                const doc = data[t];
                const meta = DOC_LABELS[t];
                return (
                  <li
                    key={t}
                    className="py-4 flex items-center justify-between gap-4 first:pt-1 last:pb-1"
                  >
                    <div>
                      <div className="font-semibold text-ink">{meta.title}</div>
                      <div className="text-sm text-ink-dim">{meta.description}</div>
                      {doc ? (
                        <div className="text-xs text-ink-dim mt-1">
                          Issued {formatDateGB(doc.issue_date)}
                          {" · "}Expires {formatDateGB(doc.expiry_date)}
                        </div>
                      ) : (
                        <div className="text-xs text-ink-dim/80 mt-1">Not yet uploaded</div>
                      )}
                    </div>
                    {doc ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={pending && openingType === t}
                        onClick={() => open(doc.file_path, t)}
                      >
                        <ExternalLink size={14} />
                        {pending && openingType === t ? "Opening…" : "Open PDF"}
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
